// 2026-03-09 (Dave) — Element History drawer (Slice 4).
// Right-side slide-out panel that shows every baseline edit event for
// a single element (from `GET /associations/{aid}/elements/{eid}/history`).
//
// Reached via the ⟳ icon in the Attachments column of the Elements grid.
// Visible to Administrator + Analyst only; view-only never sees the
// icon and the API gates the endpoint too (403).
import React, { useEffect, useState } from "react";
import axios from "axios";
import { X, History, Loader2, ArrowRight } from "lucide-react";
import { fmtDateTime } from "../lib/formatters";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Human labels for the 4 tracked baseline fields. Any other field name
// slides through as-is (e.g. create/restore/soft_delete summaries).
const FIELD_LABEL = {
  quantity: "Quantity",
  current_cost: "Current Cost",
  useful_life_when_new: "Useful Life When New",
  remaining_useful_life: "Remaining Useful Life",
};

function fmtValue(field, v) {
  if (v === null || v === undefined) return "∅";
  if (field === "current_cost") {
    const n = Number(v);
    if (Number.isFinite(n)) {
      return n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 });
    }
  }
  if (typeof v === "number") {
    return Number.isInteger(v) ? String(v) : v.toFixed(2);
  }
  return String(v);
}

export default function ElementHistoryDrawer({ aid, element, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await axios.get(
          `${API}/associations/${aid}/elements/${element.element_id}/history`,
        );
        if (!cancelled) setItems(Array.isArray(r.data) ? r.data : []);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Failed to load history.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [aid, element.element_id]);

  // Escape closes.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const label = element.element_display_id
    ? `${element.element_display_id} · ${element.name}`
    : element.name;

  return (
    <div
      className="fixed inset-0 z-50 flex"
      data-testid="element-history-drawer"
    >
      {/* Backdrop */}
      <div
        className="flex-1 bg-black/30"
        onClick={onClose}
        data-testid="element-history-backdrop"
      />
      {/* Panel */}
      <aside
        className="w-[420px] max-w-[94vw] bg-white shadow-2xl border-l-2 border-navy flex flex-col"
        style={{ animation: "slideInRight 200ms ease-out" }}
      >
        <div
          className="flex items-center justify-between px-4 py-3 border-b-2"
          style={{ borderColor: "#EA6A47", background: "var(--u-navy-wash)" }}
        >
          <div className="flex items-center gap-2 min-w-0">
            <History size={14} className="text-navy shrink-0" />
            <div className="min-w-0">
              <div className="text-[13px] font-semibold text-navy leading-none">
                Element History
              </div>
              <div className="text-[10.5px] text-slate mt-0.5 truncate" title={label}>
                {label}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate hover:text-navy shrink-0"
            data-testid="element-history-close"
            title="Close (Esc)"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-auto px-3 py-2">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-slate">
              <Loader2 size={16} className="animate-spin mr-2" /> Loading…
            </div>
          ) : error ? (
            <div className="text-[12px] text-coral p-3 border border-coral/30 rounded-[4px] bg-coral/5">
              {error}
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-8 text-slate text-[12px]">
              No history yet. Baseline edits, creates, and deletions will
              appear here.
            </div>
          ) : (
            <ul className="flex flex-col gap-1.5">
              {items.map((row) => {
                const before = row.before_json || {};
                const after = row.after_json || {};
                // Field-level rows (baseline edit) put exactly one key
                // in each dict. Fall through for create / restore /
                // soft_delete / purge rows which use different shapes.
                const beforeKey = Object.keys(before)[0];
                const afterKey = Object.keys(after)[0];
                const isFieldEdit =
                  row.action === "update" &&
                  beforeKey &&
                  afterKey &&
                  beforeKey === afterKey &&
                  beforeKey in FIELD_LABEL;

                const actor =
                  row.actor_name || row.actor_email || "System";

                return (
                  <li
                    key={row.event_id}
                    className="px-2.5 py-2 border border-rule rounded-[4px] hover:bg-navy-wash/40"
                    data-testid={`history-row-${row.event_id}`}
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-[11.5px] font-semibold text-navy">
                        {isFieldEdit
                          ? FIELD_LABEL[beforeKey]
                          : (row.summary || row.action || "change").split(":")[0]}
                      </span>
                      <span className="text-[10px] text-slate tabular-nums whitespace-nowrap">
                        {row.at ? fmtDateTime(row.at) : "—"}
                      </span>
                    </div>

                    {isFieldEdit ? (
                      <div className="mt-1 flex items-center gap-2 font-mono text-[11px] tabular-nums">
                        <span
                          className="px-1.5 py-0.5 rounded-[3px] text-slate line-through"
                          style={{ background: "rgba(234,106,71,0.08)" }}
                          title="Old value"
                        >
                          {fmtValue(beforeKey, before[beforeKey])}
                        </span>
                        <ArrowRight size={12} className="text-slate shrink-0" />
                        <span
                          className="px-1.5 py-0.5 rounded-[3px] text-navy font-semibold"
                          style={{ background: "rgba(74,157,199,0.14)" }}
                          title="New value"
                        >
                          {fmtValue(afterKey, after[afterKey])}
                        </span>
                      </div>
                    ) : (
                      <div className="mt-1 text-[11px] text-slate">
                        {row.summary || row.action}
                      </div>
                    )}

                    <div className="mt-1 text-[10px] text-slate">
                      by <span className="text-navy">{actor}</span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div
          className="px-4 py-2 border-t border-rule text-[10.5px] text-slate flex items-center justify-between"
          style={{ background: "var(--u-navy-wash)" }}
        >
          <span>{items.length} event{items.length === 1 ? "" : "s"}</span>
          <span className="italic">Newest first · last 500 shown</span>
        </div>
      </aside>
    </div>
  );
}
